import React, { useState, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, Video, Move, Edit2, Trash2 } from 'lucide-react';
import { api } from '../../contexts/AuthContext';
import Swal from 'sweetalert2';

interface GalleryItem {
  id: number;
  file_type: 'image' | 'video';
  file_path: string;
  thumbnail_path?: string;
  title?: string;
  description?: string;
  sort_order: number;
  status: 'active' | 'inactive';
}

interface TalentGalleryProps {
  workerId: number;
  readOnly?: boolean;
}

const TalentGallery: React.FC<TalentGalleryProps> = ({ workerId, readOnly = false }) => {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingItem, setEditingItem] = useState<GalleryItem | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    fetchGalleryItems();
  }, [workerId]);

  const fetchGalleryItems = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/talent/${workerId}/gallery`);
      if (response.data.success) {
        setItems(response.data.items);
      }
    } catch (error) {
      console.error('Failed to fetch gallery items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files: FileList, type: 'image' | 'video') => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', files[0]);
      formData.append('file_type', type);

      // If it's a video, create and append thumbnail
      if (type === 'video') {
        const video = document.createElement('video');
        video.src = URL.createObjectURL(files[0]);
        await new Promise(resolve => video.addEventListener('loadeddata', resolve));
        video.currentTime = 1; // Get thumbnail from 1 second in
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d')?.drawImage(video, 0, 0);
        const thumbnailBlob = await new Promise<Blob | null>(resolve => 
          canvas.toBlob(resolve, 'image/jpeg', 0.7)
        );
        if (thumbnailBlob) {
          formData.append('thumbnail', thumbnailBlob, 'thumbnail.jpg');
        }
      }

      const response = await api.post(`/talent/${workerId}/gallery`, formData);
      if (response.data.success) {
        fetchGalleryItems();
      }
    } catch (error) {
      console.error('Failed to upload file:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (itemId: number) => {
    const result = await Swal.fire({
      title: 'Confirm Delete',
      text: 'Are you sure you want to delete this item?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d'
    });

    if (!result.isConfirmed) return;

    try {
      const response = await api.delete(`/talent/${workerId}/gallery/${itemId}`);
      if (response.data.success) {
        fetchGalleryItems();
        Swal.fire({
          title: 'Success!',
          text: 'Item deleted successfully',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#28a745'
        });
      }
    } catch (error) {
      console.error('Failed to delete gallery item:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to delete item',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc3545'
      });
    }
  };

  const handleEdit = async (item: GalleryItem) => {
    setEditingItem(item);
    setShowEditModal(true);
  };

  const handleUpdate = async (itemId: number, data: Partial<GalleryItem>) => {
    try {
      const response = await api.put(`/talent/${workerId}/gallery/${itemId}`, data);
      if (response.data.success) {
        fetchGalleryItems();
        setShowEditModal(false);
        setEditingItem(null);
      }
    } catch (error) {
      console.error('Failed to update gallery item:', error);
    }
  };

  const handleReorder = async (draggedId: number, targetId: number) => {
    const newItems = [...items];
    const draggedIndex = items.findIndex(item => item.id === draggedId);
    const targetIndex = items.findIndex(item => item.id === targetId);
    
    // Swap items
    [newItems[draggedIndex], newItems[targetIndex]] = [newItems[targetIndex], newItems[draggedIndex]];
    
    // Update sort order
    const updatedItems = newItems.map((item, index) => ({
      id: item.id,
      sort_order: index
    }));

    try {
      const response = await api.put(`/talent/${workerId}/gallery/reorder`, {
        items: updatedItems
      });
      if (response.data.success) {
        setItems(newItems);
      }
    } catch (error) {
      console.error('Failed to reorder gallery items:', error);
    }
  };

  if (loading) {
    return <div className="loading-spinner" />;
  }

  return (
    <div className="talent-gallery">
      {!readOnly && (
        <div className="gallery-actions" style={{ marginBottom: '20px' }}>
          <label className="file-upload-button">
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => e.target.files && handleFileUpload(e.target.files, 'image')}
            />
            <div className="btn-secondary">
              <ImageIcon size={18} />
              Upload Image
            </div>
          </label>

          <label className="file-upload-button">
            <input
              type="file"
              accept="video/*"
              style={{ display: 'none' }}
              onChange={(e) => e.target.files && handleFileUpload(e.target.files, 'video')}
            />
            <div className="btn-secondary">
              <Video size={18} />
              Upload Video
            </div>
          </label>
        </div>
      )}

      <div className="gallery-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '20px'
      }}>
        {items.map((item) => (
          <div
            key={item.id}
            className="gallery-item"
            draggable={!readOnly}
            onDragStart={(e) => e.dataTransfer.setData('itemId', item.id.toString())}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const draggedId = parseInt(e.dataTransfer.getData('itemId'));
              handleReorder(draggedId, item.id);
            }}
            style={{
              position: 'relative',
              borderRadius: '8px',
              overflow: 'hidden',
              border: '1px solid var(--border-color)',
              backgroundColor: 'var(--background-light)'
            }}
          >
            {item.file_type === 'video' ? (
              <video
                src={`${process.env.REACT_APP_API_URL}${item.file_path}`}
                poster={item.thumbnail_path ? `${process.env.REACT_APP_API_URL}${item.thumbnail_path}` : undefined}
                controls
                style={{ width: '100%', height: '200px', objectFit: 'cover' }}
              />
            ) : (
              <img
                src={`${process.env.REACT_APP_API_URL}${item.file_path}`}
                alt={item.title || 'Gallery item'}
                style={{ width: '100%', height: '200px', objectFit: 'cover' }}
              />
            )}

            {!readOnly && (
              <div className="gallery-item-actions" style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                display: 'flex',
                gap: '8px'
              }}>
                <button
                  className="btn-icon"
                  onClick={() => handleEdit(item)}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'var(--background-medium)',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <Edit2 size={14} />
                </button>
                <button
                  className="btn-icon"
                  onClick={() => handleDelete(item.id)}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'var(--danger-color)',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <Trash2 size={14} color="white" />
                </button>
              </div>
            )}

            {item.title && (
              <div style={{ padding: '10px' }}>
                <div style={{ fontWeight: '500' }}>{item.title}</div>
                {item.description && (
                  <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>
                    {item.description}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {showEditModal && editingItem && (
        <div className="modal">
          <div className="modal-content">
            <h2>Edit Gallery Item</h2>
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={editingItem.title || ''}
                onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={editingItem.description || ''}
                onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                className="form-control"
              />
            </div>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingItem(null);
                }}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={() => handleUpdate(editingItem.id, {
                  title: editingItem.title,
                  description: editingItem.description
                })}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {uploading && (
        <div className="upload-overlay">
          <div className="spinner" />
          <div>Uploading...</div>
        </div>
      )}
    </div>
  );
};

export default TalentGallery; 